/**
 * canvas-engine/presets/presetData/educationPresets.js
 *
 * TRANSCRIBED Education default look-and-feel VALUES (data only).
 *
 * ZERO-REUSE CONSTRAINT (Task 3.1, design "Default look-and-feel: data, not
 * code"): this module imports NOTHING. The default content strings, colors,
 * spacing, radius, backgrounds, and default imagery below are *transcribed /
 * duplicated* from the Default_Source (`educationTemplateDefaults.js` +
 * `ConsoleEducationPreview`) as plain DATA so a freshly added section's default
 * look matches today's defaults — with zero code dependency on the existing
 * builder. No React, no rendering, no side effects.
 *
 * Consumed by the own decomposition adapters (Task 3.2 `buildSectionCanvas` +
 * Task 3.3 `education.js`), which lay these values out as individually editable
 * Canvas_Elements within the fixed Reference_Width (1120px) frame. Element
 * positioning is the adapters' job; this file only provides faithful
 * content + style VALUES per section type.
 *
 * Descriptor shape (uniform across every presetData module):
 *   {
 *     sectionType,            // source section type id
 *     label,                  // human label
 *     domain: 'education',
 *     style:  { background, textColor, accentColor, secondaryColor,
 *               padding, backgroundImage?, ...frame style values },
 *     layout: { align, width, columns? },          // layout hints
 *     content:{ eyebrow?, heading?, subheading?, body?, blocks?, cards?,
 *               items?, actions?, images?, ...section-specific copy }
 *   }
 *
 * Images are stored as asset references `{ storedLink, displayLink }` to match
 * the engine's single asset representation (Req 22.4).
 *
 * Requirements: 3.6, 3.7, 5.1, 5.2, 5.6, 5.7
 */

const BASE_URL =
  'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/';

/** Default imagery (transcribed from educationTemplateDefaults `educationImages`). */
export const educationImages = {
  AVPS_LOGO: `${BASE_URL}AVPS.png.png`,
  BILD: `${BASE_URL}Bild.png`,
  BRIDGING_THE_KNOWLEDGE_GAP: `${BASE_URL}BridgingTheKnowledgeGap.png`,
  BUILDING: `${BASE_URL}building.png`,
  CLOUD_COMPUTING: `${BASE_URL}cloud-computing.png`,
  CONTACT:
    'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Education+Domain+Assets/customer-care.jpg',
  CORE_JAVA_BANNER: `${BASE_URL}coreJAVABanner.png`,
  CLASSROOM: `${BASE_URL}classroom.png`,
  CURRICULUM: `${BASE_URL}curriculum.png`,
  E_LEARNING: `${BASE_URL}e-learning.png`,
  E_LEARNING_ALT: `${BASE_URL}elearning.png`,
  GROOMING: `${BASE_URL}Grooming.png`,
  GUIDANCE: `${BASE_URL}guidance.png`,
  INCLUSIVITY: `${BASE_URL}inclusivity.png`,
  INSIGHTS: `${BASE_URL}Insights.png`,
  JAVA_BANNER: `${BASE_URL}javaBanner.png`,
  JOIN_US_ABOUT: `${BASE_URL}joinUSAbout.png`,
  KRUMAGO_LOGO: `${BASE_URL}Krumagoimg.png`,
  LANDING_PAGE: `${BASE_URL}landingPage.jpg`,
  PERSONAL_TRAINER: `${BASE_URL}personal-trainer.png`,
  WOMEN_IMG: `${BASE_URL}womenImg.jpg`,
};

/** Theme-level style values (radius/shadow/spacing) transcribed from `themeDefaults`. */
export const educationTheme = {
  primary: '#3B9394',
  secondary: '#87927c',
  accent: '#23196E',
  background: '#ffffff',
  surface: '#ffffff',
  color: '#1f2937',
  mutedColor: '#4b5563',
  borderColor: '#e5e7eb',
  headingFontFamily: 'Inter, Arial, sans-serif',
  fontFamily: 'Inter, Arial, sans-serif',
  baseFontSize: '16px',
  headingWeight: '800',
  bodyWeight: '400',
  lineHeight: '1.6',
  sectionSpacing: '80px',
  gridGap: '24px',
  buttonRadius: '8px',
  cardRadius: '16px',
  cardShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
  imageRadius: '16px',
  headerHeight: '70px',
};

// ── Local pure data helpers (no imports, mirror the Default_Source factories) ──
const img = (url = '') => ({ storedLink: '', displayLink: url });
const action = (label, url, variant = 'primary') => ({ label, url, variant });
const block = (title, description = '', accentColor = '#3B9394', icon = '', url) => ({
  title,
  description,
  accentColor,
  icon,
  ...(url ? { url } : {}),
});
const category = (title, description, imageUrl) => ({
  title,
  description,
  imageUrl,
  imageContent: img(imageUrl),
  url: '/course',
});
const course = (title, description, level, imageUrl) => ({
  title,
  description,
  level,
  imageUrl,
  imageContent: img(imageUrl),
  url: '/course-detail',
});

/**
 * EDUCATION_PRESETS — per-section-type default descriptors.
 * Keyed by the Default_Source section type id.
 */
export const EDUCATION_PRESETS = {
  educationHero: {
    sectionType: 'educationHero',
    label: 'Education hero',
    domain: 'education',
    style: {
      background: '#111827',
      textColor: '#ffffff',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '80px 32px',
      minHeight: '100vh',
      backgroundImage: img(educationImages.LANDING_PAGE),
    },
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      eyebrow: '',
      heading: 'Transform Your Career with Expert-Led Courses',
      headingParts: {
        prefix: 'Transform Your Career with',
        accent: 'Expert-Led',
        suffix: 'Courses',
      },
      subheading:
        'Join thousands of professionals who have accelerated their careers with our industry-relevant curriculum, hands-on projects, and personalized mentorship.',
      highlights: [
        block('Industry Experts'),
        block('Flexible Schedule'),
        block('Hands-on Projects'),
        block('Career Support'),
      ],
      actions: [
        action('Explore All Courses', '/course', 'primary'),
        action('Sign Up Now', '/sign-up', 'outline'),
      ],
      stats: [{ value: '100%', label: 'Master your skill' }],
      highlightCardTitle: 'Course Highlights',
      highlightButtonLabel: 'Start Learning Journey',
      highlightButtonUrl: '/course',
      cards: [
        block(
          'Comprehensive Curriculum',
          'Industry-aligned courses with latest technologies and frameworks',
          '#87927c',
          'Book',
        ),
        block(
          'Flexible Schedule',
          'Learn at your own pace with 24/7 access to course materials',
          '#3B9394',
          'Clock',
        ),
        block('1:1 Mentorship', 'Personalized guidance from industry experts', '#87927c', 'Mentor'),
        block(
          'Career Services',
          'Resume building, interview prep, and job placement support',
          '#3B9394',
          'Career',
        ),
      ],
    },
  },

  educationCategoryGrid: {
    sectionType: 'educationCategoryGrid',
    label: 'Course category grid',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1a202c',
      accentColor: '#3B9394',
      padding: '64px 32px',
    },
    layout: { align: 'left', width: 'wide', columns: 3 },
    content: {
      heading: 'Learn essential career and life skills',
      subheading:
        'AVPS academy helps you build in-demand skills fast and advance your career in a changing job market.',
      emptyStateTitle: 'Course categories will appear here',
      cards: [
        category(
          'Full Stack Development',
          'Master frontend, backend, databases, and deployment.',
          educationImages.JAVA_BANNER,
        ),
        category(
          'Cloud & DevOps',
          'Build modern deployment and cloud operations skills.',
          educationImages.CLOUD_COMPUTING,
        ),
        category(
          'Career Readiness',
          'Strengthen interview, portfolio, and workplace skills.',
          educationImages.GUIDANCE,
        ),
      ],
    },
  },

  educationFeatureGrid: {
    sectionType: 'educationFeatureGrid',
    label: 'Why choose academy',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      padding: '80px 16px',
    },
    layout: { align: 'center', width: 'contained', columns: 2 },
    content: {
      heading: 'Why Choose Our Academy',
      headingParts: { prefix: 'Why', accent: 'Choose', suffix: 'Our Academy' },
      subheading:
        'Discover how our comprehensive approach to education transforms careers and bridges the gap between learning and real-world success.',
      cards: [
        block(
          'Bridging the Knowledge Gap',
          "The IT industry evolves rapidly with new technologies and trends. We equip you with cutting-edge skills needed to thrive in today's competitive landscape.",
          '#3B9394',
          'Graduate',
          '/BridgingTheKnowledgeGap',
        ),
        block(
          'Insights & Effective Engagement',
          'Learning thrives through collaboration, personalized feedback, and engaging learning experiences.',
          '#87927c',
          'Insight',
          '/InsightsAndEngagement',
        ),
        block(
          'Build Industry Experience',
          'Bridge theoretical knowledge with practical application through real-world skills demanded by leading teams.',
          '#3B9394',
          'Build',
          '/BuildIndustryExperience',
        ),
        block(
          'Technical & Soft Skills Development',
          'Build in-demand technical expertise while honing essential soft skills for any professional environment.',
          '#87927c',
          'Career',
          '/GroomingSkills',
        ),
      ],
    },
  },

  educationFeaturedCourses: {
    sectionType: 'educationFeaturedCourses',
    label: 'Featured courses',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      padding: '64px 0',
    },
    layout: { align: 'center', width: 'contained', columns: 4 },
    content: {
      heading: 'Featured Courses',
      subheading: 'Explore popular courses selected from the active academy catalog.',
      emptyStateTitle: 'Featured courses will appear after courses are published.',
      cards: [
        course('Core Java', 'Build a strong Java foundation with projects.', 'Beginner', educationImages.CORE_JAVA_BANNER),
        course('Full Stack Java', 'Create modern web applications end to end.', 'Intermediate', educationImages.JAVA_BANNER),
        course('Cloud Fundamentals', 'Learn cloud concepts and deployment basics.', 'Beginner', educationImages.CLOUD_COMPUTING),
        course('Career Grooming', 'Prepare for interviews and workplace success.', 'All levels', educationImages.GROOMING),
      ],
      // Dynamic binding marker — adapters bind this rail to live courses.
      binding: { source: 'validCourses', limit: 4, route: '/course-detail' },
    },
  },

  educationMissionVision: {
    sectionType: 'educationMissionVision',
    label: 'Mission and vision',
    domain: 'education',
    style: {
      background: '#111827',
      textColor: '#ffffff',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '80px 16px',
      backgroundImage: img(educationImages.LANDING_PAGE),
    },
    layout: { align: 'center', width: 'contained', columns: 2 },
    content: {
      heading: 'Our Mission & Vision',
      headingParts: { prefix: 'Our', accent: 'Mission', suffix: '& Vision' },
      subheading:
        'Driving innovation in education to create meaningful impact and transform lives through learning',
      missionTitle: 'Our Mission',
      missionDescription:
        'To democratize quality education and empower individuals with the skills needed to thrive in the digital economy through innovative learning solutions and personalized mentorship.',
      visionTitle: 'Our Vision',
      visionDescription:
        'Creating a world where anyone, anywhere can transform their life through accessible, high-quality education that bridges the gap between learning and real-world success.',
      quote:
        'Education is the passport to the future, for tomorrow belongs to those who prepare for it today.',
      quoteAuthor: 'Founder & CEO',
      valuesTitle: 'Our Core Values',
      cards: [
        block('Innovation', 'Embracing cutting-edge teaching methodologies', '#3B9394', 'Idea'),
        block('Collaboration', 'Building learning communities that support growth', '#87927c', 'Team'),
        block('Excellence', 'Delivering world-class educational experiences', '#3B9394', 'Star'),
      ],
      actions: [action('Learn More About Our Journey', '/about', 'primary')],
    },
  },

  educationJoinCommunity: {
    sectionType: 'educationJoinCommunity',
    label: 'Learning community CTA',
    domain: 'education',
    style: {
      background: '#f8fafc',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '80px 16px',
    },
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      badge: 'Your Success Our Goal',
      heading: 'Join Our Learning Community',
      headingParts: { prefix: 'Join Our', accent: 'Learning', suffix: 'Community' },
      subheading:
        'Connect with like-minded professionals, enhance your skills with industry-relevant courses, and accelerate your career growth in a supportive learning environment.',
      images: [img(educationImages.WOMEN_IMG)],
      imageAlt: 'Professional joining our learning community',
      blocks: [
        block('Expert-Led Courses', '', '#16a34a'),
        block('Career Support', '', '#23196E'),
        block('Flexible Learning', '', '#87927c'),
        block('Networking Opportunities', '', '#f97316'),
      ],
      actions: [
        action('Start Learning Journey', '/sign-in', 'primary'),
        action('Explore Courses', '/course', 'outline'),
      ],
      stats: [
        { value: '100%', label: 'Satisfaction' },
        { value: '1:1', label: 'Mentorship' },
      ],
    },
  },

  educationInfoPage: {
    sectionType: 'educationInfoPage',
    label: 'Education info page',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '72px 24px',
    },
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Build career-ready skills',
      subheading:
        'Customize this informational Education page with rich, structured blocks.',
      images: [img(educationImages.BRIDGING_THE_KNOWLEDGE_GAP)],
      cards: [
        block('Editable section', 'Add, remove, duplicate, or rewrite content blocks from the inspector.', '#3B9394', 'Edit'),
        block('Safe custom code', 'Use scoped custom HTML/CSS when the structured controls are not enough.', '#87927c', 'Code'),
        block('Live academy rendering', 'Published templates render through the same section schema in academy-ui.', '#3B9394', 'Render'),
      ],
      actions: [action('Explore Courses', '/course', 'primary')],
    },
  },

  // ── Education info pages (Flow_Pages) ──
  // Transcribed faithfully from the Default_Source legacy KeyFeatures components
  // (BridgingTheKnowledgeGap / InsightsAndEffectiveEngagement /
  // BuildIndustryExperience / GroomingTechnicalAndSoftSkills) rendered through the
  // EducationInfoPage look (hero image split + numbered content blocks + CTA).
  educationBridgingKnowledgeGap: {
    sectionType: 'educationBridgingKnowledgeGap',
    label: 'Bridging the knowledge gap',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '72px 24px',
    },
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Bridging the knowledge gap',
      subheading:
        "The IT industry is a dynamic landscape, constantly evolving with new technologies and trends. This rapid pace often creates a significant knowledge gap between academic learning and real-world industry demands. At AVPS Academy, we're committed to bridging this gap and equipping you with the skills and mindset needed to thrive in the IT world.",
      images: [img(educationImages.BRIDGING_THE_KNOWLEDGE_GAP)],
      cards: [
        block(
          'Close the Theory Practice Gap',
          'We bridge the gap between theoretical concepts and real-world problem-solving through hands-on projects and simulations.',
          '#3B9394',
        ),
        block(
          'Industry-Aligned Curriculum',
          'Our courses are developed in collaboration with industry experts to ensure they align with current trends and job requirements.',
          '#87927c',
        ),
        block(
          'Mentorship and Guidance',
          'Our experienced mentors provide personalized support, offering valuable insights and career guidance.',
          '#3B9394',
        ),
        block(
          'Soft Skills Development',
          'We emphasize the importance of communication, teamwork, and problem-solving skills, essential for success in the IT industry.',
          '#87927c',
        ),
      ],
      actions: [action('Enroll Now', '/products', 'primary')],
    },
  },

  educationInsightsEngagement: {
    sectionType: 'educationInsightsEngagement',
    label: 'Insights and effective engagement',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '72px 24px',
    },
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Insights and effective engagement',
      subheading:
        'At AVPS Academy, we believe that effective learning is a collaborative process between the student and the instructor. Our commitment to providing exceptional IT training is underpinned by our dedication to providing clear insights into your learning journey and fostering engaging interactions.',
      images: [img(educationImages.INSIGHTS)],
      cards: [
        block(
          'Real-time Progress Tracking',
          'Our advanced learning management system offers real-time visibility into your course progress. You can monitor your performance, identify areas of strength and weakness, and stay motivated with every milestone achieved.',
          '#3B9394',
        ),
        block(
          'Personalized Feedback',
          'We provide constructive and actionable feedback on your assignments and projects. This feedback is tailored to your individual learning style and goals, helping you improve and grow.',
          '#87927c',
        ),
        block(
          'Open Communication Channels',
          "Oe encourage open and honest communication between you and your instructors. Whether it's through online forums, email, or one-on-one meetings, we are always available to answer your questions and address your concerns.",
          '#3B9394',
        ),
        block(
          'Interactive Learning Experiences',
          'Our courses are designed to be engaging and interactive. Through a combination of hands-on exercises, group projects, and real-world simulations, we create a dynamic learning environment that fosters critical thinking and problem-solving skills.',
          '#87927c',
        ),
      ],
      actions: [action('Enroll Now', '/products', 'primary')],
    },
  },

  educationIndustryExperience: {
    sectionType: 'educationIndustryExperience',
    label: 'Build industry experience',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '72px 24px',
    },
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Build Industry experience',
      subheading:
        'At AVPS Academy, we believe that learning should extend beyond the classroom. Our "Build Industry Experience" program is designed to bridge the gap between theoretical knowledge and practical application, equipping you with the real-world skills demanded by top IT companies.',
      images: [img(educationImages.BILD)],
      cards: [
        block(
          'Complex IT Challenges',
          'Tackle real-world problems that mirror those faced by professionals.',
          '#3B9394',
        ),
        block(
          'Cutting-Edge Technologies',
          'Work with the latest tools and platforms used in the industry.',
          '#87927c',
        ),
        block(
          'Collaborative Teamwork',
          'Develop essential soft skills by working in teams on diverse projects.',
          '#3B9394',
        ),
      ],
      actions: [action('Enroll Now', '/products', 'primary')],
    },
  },

  educationGroomingSkills: {
    sectionType: 'educationGroomingSkills',
    label: 'Grooming technical and soft skills',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '72px 24px',
    },
    layout: { align: 'left', width: 'contained', columns: 2 },
    content: {
      heading: 'Grooming technical and soft skills',
      subheading:
        "At AVPS Academy, we believe that a successful IT professional needs more than just technical knowledge. That's why we've designed our curriculum to not only equip you with in-demand technical skills but also to hone your soft skills, making you a well rounded and industry-ready talent.",
      images: [img(educationImages.GROOMING)],
      cards: [
        block(
          'Core Technical Skills',
          'Build a strong foundation in your chosen IT specialization.',
          '#3B9394',
        ),
        block(
          'Hands-On Experience',
          'Apply theoretical knowledge through practical projects and simulations.',
          '#87927c',
        ),
        block(
          'Emerging Technologies',
          'Stay updated with the latest industry trends and advancements.',
          '#3B9394',
        ),
        block(
          'Soft Skill Development',
          'Enhance communication, teamwork, problem-solving, and leadership abilities.',
          '#87927c',
        ),
      ],
      actions: [action('Enroll Now', '/products', 'primary')],
    },
  },

  // "products" (courses page shell)
  educationProductsPage: {
    sectionType: 'educationProductsPage',
    label: 'Courses page shell',
    domain: 'education',
    style: {
      background: '#f8fafc',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '56px 24px',
      maxWidth: '1536px',
    },
    layout: { align: 'left', width: 'contained' },
    content: {
      heading: 'Courses',
      subheading: 'Discover expert-led courses that match your career goals.',
      // Dynamic shell — live courses listing renders inside this chrome.
      binding: { source: 'validCourses' },
    },
  },

  educationCourseDetail: {
    sectionType: 'educationCourseDetail',
    label: 'Course detail shell',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      padding: '56px 24px',
      maxWidth: '1536px',
    },
    layout: { align: 'left', width: 'contained' },
    content: {
      title: 'Core Java',
      subtitle: 'Build a strong Java foundation with expert-led projects.',
      componentKey: 'CourseInformation',
      images: [img(educationImages.CORE_JAVA_BANNER)],
      imageGallery: [
        { title: 'Core Java banner', imageContent: img(educationImages.CORE_JAVA_BANNER) },
        { title: 'Full stack Java', imageContent: img(educationImages.JAVA_BANNER) },
        { title: 'Cloud computing', imageContent: img(educationImages.CLOUD_COMPUTING) },
      ],
      enrollLabel: 'Enroll Now',
      processingLabel: 'Processing...',
      deliveryTitle: 'Instructor-led Online',
      deliveryDescription: 'Live virtual sessions with instructor',
      categoryTitle: 'Full Stack Development',
      categoryDescription: 'Discover exciting Full Stack Development experiences!',
      scheduleTitle: 'Explore Your Schedule Options',
      scheduleLinkTitle: 'Flexible schedule',
      scheduleLinkLabel: 'Pick your schedule',
      aboutTitle: 'About the course',
      aboutLinkLabel: 'Learn more',
      descriptionTitle: 'About the course',
      description:
        '<p>Master core Java concepts, object-oriented programming, collections, streams, exception handling, and project-ready coding practices through guided sessions and hands-on work.</p>',
      scheduleOptions: [
        { title: 'Weekend Batch', description: 'Saturday and Sunday live classes', date: 'Starts Jun 15', time: '10:00 AM - 1:00 PM' },
        { title: 'Weekday Evening', description: 'Instructor-led weekday classes', date: 'Starts Jun 20', time: '7:00 PM - 9:00 PM' },
      ],
      curriculumTitle: 'Core Java has 4 chapters and 2 exams',
      curriculum: [
        {
          title: 'Java Fundamentals',
          description: 'Syntax, variables, operators, and control flow.',
          type: 'chapter',
          contents: ['Language setup', 'Data types and variables', 'Control statements'],
        },
        {
          title: 'Object-Oriented Programming',
          description: 'Classes, objects, inheritance, polymorphism, and abstraction.',
          type: 'chapter',
          contents: ['Classes and objects', 'Inheritance', 'Interfaces and abstraction'],
        },
        {
          title: 'Collections and Streams',
          description: 'Lists, maps, sets, lambdas, and stream pipelines.',
          type: 'chapter',
          contents: ['Collections framework', 'Generics', 'Streams'],
        },
        {
          title: 'Spring Boot Project',
          description: 'Build a small service with database integration.',
          type: 'chapter',
          contents: ['REST APIs', 'Persistence', 'Project review'],
        },
        {
          title: 'Core Java Assessment',
          description: 'Practice assessment for Java fundamentals.',
          type: 'exam',
          contents: [],
        },
        {
          title: 'Capstone Evaluation',
          description: 'Final project and review.',
          type: 'exam',
          contents: [],
        },
      ],
      binding: { source: 'courseDetail' },
    },
  },

  educationCart: {
    sectionType: 'educationCart',
    label: 'Cart shell',
    domain: 'education',
    style: { background: '#ffffff', textColor: '#1f2937', padding: '0' },
    layout: { align: 'left', width: 'full' },
    content: {
      title: 'Your learning cart',
      subtitle: 'Review selected courses before checkout.',
      componentKey: 'Cart',
      binding: { source: 'cart' },
    },
  },

  educationContact: {
    sectionType: 'educationContact',
    label: 'Contact page shell',
    domain: 'education',
    style: { background: '#ffffff', textColor: '#1f2937', padding: '96px 24px 64px' },
    layout: { align: 'left', width: 'contained' },
    content: {
      heading: 'Get in touch',
      headingParts: { prefix: 'Get in', accent: 'touch' },
      subheading: "Have questions about admissions or courses? We're here to guide you.",
      images: [img(educationImages.CONTACT)],
      imageHeading: "Let's Connect",
      imageSubtitle: 'Building relationships that matter',
      phoneTitle: 'Phone',
      phoneValue: '+1 244-5333-7783',
      emailTitle: 'Email',
      emailValue: 'info@avpstech.com',
      addressTitle: 'Address',
      addressValue: 'AVPS Academy',
      formTitle: 'Send us a Message',
      submitLabel: 'Send Message',
      fields: [
        { label: 'First Name', type: 'text', visible: true },
        { label: 'Last Name', type: 'text', visible: true },
        { label: 'Email Address', type: 'email', required: true, visible: true },
        { label: 'Phone Number', type: 'tel', visible: true },
        { label: 'Message', type: 'textarea', required: true, visible: true },
      ],
    },
  },

  // ── Additional Education shells (covered for completeness) ──
  educationAbout: {
    sectionType: 'educationAbout',
    label: 'About page shell',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '88px 24px',
    },
    layout: { align: 'left', width: 'contained' },
    content: {
      eyebrow: 'About Us',
      heading: 'AVPS Academy',
      subheading:
        'At the AVPS Academy, we are committed to providing unparalleled educational experiences for our students. Our esteemed faculty members are experts in their respective fields and are dedicated to helping students achieve their academic and personal goals.',
      images: [img(educationImages.E_LEARNING)],
      aboutSectionTitle: 'Why Choose the AVPS Academy?',
      showWhyChoose: true,
      showSignupBanner: true,
      signupImage: img(educationImages.JOIN_US_ABOUT),
      signupButtonLabel: 'Sign Up',
      signupButtonUrl: '/sign-in',
      cards: [
        { ...block('Cutting-Edge Curriculum', 'Immerse your child in an advanced curriculum designed to foster innovation and adaptability in a rapidly changing world.', '#3B9394', 'Curriculum'), imageContent: img(educationImages.CURRICULUM) },
        { ...block('Expert Faculty', 'Learn from a team of passionate, experienced educators dedicated to nurturing individual student potential.', '#3B9394', 'Faculty'), imageContent: img(educationImages.PERSONAL_TRAINER) },
        { ...block('Small Class Sizes', 'Enjoy personalized learning experiences in small class settings that prioritize student engagement and interaction.', '#3B9394', 'Class'), imageContent: img(educationImages.CLASSROOM) },
        { ...block('State-of-the-Art Facilities', 'Discover our cutting-edge learning environments, featuring top-tier technology and resources for modern education.', '#3B9394', 'Facilities'), imageContent: img(educationImages.BUILDING) },
        { ...block('Inclusive and Diverse Community', 'Be part of a community that values and celebrates diversity, creating a safe and inclusive space for all students.', '#3B9394', 'Community'), imageContent: img(educationImages.INCLUSIVITY) },
        { ...block('Parental Involvement', "Partner with us in your child's education, fostering a collaborative relationship that strengthens their academic journey.", '#3B9394', 'Guidance'), imageContent: img(educationImages.GUIDANCE) },
        { ...block('Strong Values-Based Education', 'Shape character and leadership through our commitment to teaching strong ethical values and social responsibility.', '#3B9394', 'Values'), imageContent: img(educationImages.E_LEARNING_ALT) },
      ],
    },
  },

  educationSummary: {
    sectionType: 'educationSummary',
    label: 'Summary shell',
    domain: 'education',
    style: { background: '#ffffff', textColor: '#1f2937', padding: '0' },
    layout: { align: 'left', width: 'full' },
    content: {
      title: 'Enrollment summary',
      subtitle: 'Track purchases, invoices, and course enrollment history.',
      componentKey: 'OrderHistory',
      paymentSummaryTitle: 'Payment Summary',
      subtotalLabel: 'Subtotal',
      taxLabel: 'Tax',
      totalLabel: 'Total',
      buttonLabel: 'Complete Payment',
      currencySymbol: '$',
      binding: { source: 'orderHistory' },
    },
  },

  educationHeaderRail: {
    sectionType: 'educationHeaderRail',
    label: 'Course header rail',
    domain: 'education',
    style: {
      background: '#ffffff',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '0',
    },
    layout: { align: 'left', width: 'full' },
    content: {
      title: 'Course search and navigation',
      subtitle: 'Live academy header rail with search, categories, and course suggestions.',
      componentKey: 'EducationDomainHeader',
      logo: img(educationImages.AVPS_LOGO),
      logoAlt: 'AVPS Academy',
      searchPlaceholder: 'Search courses, categories...',
      searchButtonLabel: 'Search',
      signInLabel: 'Sign In',
      accountLabel: 'Account',
      cartLabel: 'Cart',
      nav: [
        { label: 'Home', route: '/', visible: true },
        { label: 'Courses', route: '/course', visible: true },
        { label: 'About', route: '/about', visible: true },
        { label: 'Contact', route: '/contact', visible: true },
      ],
      // Dynamic binding marker — adapters bind this rail to live courses.
      binding: { source: 'validCourses' },
    },
  },

  educationAuthEntry: {
    sectionType: 'educationAuthEntry',
    label: 'Auth entry shell',
    domain: 'education',
    style: {
      background: '#f8fafc',
      textColor: '#1f2937',
      accentColor: '#3B9394',
      secondaryColor: '#87927c',
      padding: '72px 24px',
    },
    layout: { align: 'center', width: 'contained' },
    content: {
      // Default reflects the sign-in mode. Sign-up copy + fields are carried in
      // `variants.signUp` (mirrors the waasAuth shape) so a sign-up section can be
      // authored; the variant block is preserved losslessly under
      // canvas.source.preserved.content and ignored by the default stack layout.
      mode: 'signIn',
      heading: 'Continue your learning journey',
      subheading: 'Sign in or create an account to access courses, schedules, and enrollment details.',
      formTitle: 'Sign in',
      submitLabel: 'Sign in',
      alternateLabel: "Need an account? Sign up",
      alternateUrl: '/sign-up',
      images: [img(educationImages.E_LEARNING)],
      imageAlt: 'Continue your learning journey',
      componentKey: 'Login',
      actions: [
        action('Sign In', '/sign-in', 'primary'),
        action('Sign Up', '/sign-up', 'outline'),
      ],
      fields: [
        { label: 'Email Address', type: 'email', required: true, visible: true },
        { label: 'Password', type: 'password', required: true, visible: true },
      ],
      variants: {
        signUp: {
          mode: 'signUp',
          heading: 'Create your account',
          subheading: 'Sign up to access courses, schedules, and enrollment details.',
          formTitle: 'Create account',
          submitLabel: 'Create account',
          alternateLabel: 'Already have an account? Sign in',
          alternateUrl: '/sign-in',
          images: [img(educationImages.JOIN_US_ABOUT)],
          imageAlt: 'Join our learning community',
          fields: [
            { label: 'First Name', type: 'text', required: true, visible: true },
            { label: 'Last Name', type: 'text', required: true, visible: true },
            { label: 'Email Address', type: 'email', required: true, visible: true },
            { label: 'Phone Number', type: 'tel', required: false, visible: true },
            { label: 'Password', type: 'password', required: true, visible: true },
          ],
        },
      },
    },
  },
};

/** Section presets list (transcribed from `educationSectionPresets`). */
export const educationSectionPresets = [
  { type: 'customHtml', title: 'Custom section', description: 'Start blank with sanitized HTML and scoped CSS.', highlight: true },
  { type: 'educationHero', title: 'Education hero', description: 'Hero with course highlights and CTAs.' },
  { type: 'educationCategoryGrid', title: 'Category grid', description: 'Editable course category cards.' },
  { type: 'educationFeatureGrid', title: 'Why choose', description: 'Landing feature cards and links.' },
  { type: 'educationFeaturedCourses', title: 'Featured courses', description: 'Course cards bound to academy catalog.' },
  { type: 'educationMissionVision', title: 'Mission', description: 'Mission, vision, quote, and values.' },
  { type: 'educationJoinCommunity', title: 'Community CTA', description: 'Image CTA with benefits and stats.' },
  { type: 'educationInfoPage', title: 'Info page', description: 'Structured content page.' },
  { type: 'educationBridgingKnowledgeGap', title: 'Bridging the knowledge gap', description: 'Info page: closing the theory-practice gap with hands-on learning.' },
  { type: 'educationInsightsEngagement', title: 'Insights & engagement', description: 'Info page: progress tracking, feedback, and engaged learning.' },
  { type: 'educationIndustryExperience', title: 'Build industry experience', description: 'Info page: real-world skills through industry-aligned projects.' },
  { type: 'educationGroomingSkills', title: 'Technical & soft skills', description: 'Info page: grooming technical expertise and soft skills.' },
  { type: 'educationProductsPage', title: 'Courses shell', description: 'Courses listing wrapper.' },
  { type: 'educationCourseDetail', title: 'Course detail shell', description: 'Course detail wrapper with editable schedule, description, and curriculum preview.' },
  { type: 'educationCart', title: 'Cart shell', description: 'Cart wrapper.' },
  { type: 'educationContact', title: 'Contact shell', description: 'Contact wrapper.' },
  { type: 'educationAbout', title: 'About shell', description: 'About page with "why choose" cards and signup banner.' },
  { type: 'educationSummary', title: 'Summary shell', description: 'Order history / enrollment summary wrapper.' },
  { type: 'educationHeaderRail', title: 'Course header rail', description: 'Search, categories, and course navigation bound to live courses.' },
  { type: 'educationAuthEntry', title: 'Auth entry', description: 'Sign-in / sign-up entry message section.' },
];

export default EDUCATION_PRESETS;
